import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { auth, firestore } from "../firebase";
import {
  mapDocToPublicListing,
  PublicListing,
  PublicListingCollection,
} from "../types/PublicListing";

export function usePublicListingByHandle(handle: string | undefined) {
  const [publicListing, setPublicListing] = useState<PublicListing | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) {
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    const col = collection(firestore, PublicListingCollection);

    // Authenticated users may be the listing owner, so include in-preview.
    // If that query is denied (not the owner), fall back to public-only.
    const statuses = auth.currentUser
      ? ["public", "in-preview"]
      : ["public"];

    const q = query(
      col,
      where("handle", "==", handle),
      where("status", "in", statuses),
      limit(1),
    );

    getDocs(q)
      .then((snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setPublicListing(mapDocToPublicListing(doc.id, doc.data()));
        } else {
          setPublicListing(null);
        }
        setError(null);
      })
      .catch(() => {
        // Authenticated non-owner: query including in-preview was denied.
        // Retry with public-only.
        const fallback = query(
          col,
          where("handle", "==", handle),
          where("status", "==", "public"),
          limit(1),
        );
        return getDocs(fallback).then((snapshot) => {
          if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            setPublicListing(mapDocToPublicListing(doc.id, doc.data()));
          } else {
            setPublicListing(null);
          }
          setError(null);
        });
      })
      .catch(() => {
        setError("Failed to load listing");
        setPublicListing(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [handle]);

  return useMemo(
    () => ({ publicListing, isLoading, error }),
    [publicListing, isLoading, error],
  );
}
